import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, ModalProps, Stack, useModals } from "@skailer-com/uikit";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface WebhookHeaderValue {
  key: string;
  value: string;
}

interface WebhookHeaderEditModalProps extends ModalProps {
  header?: WebhookHeaderValue;
  existingKeys: string[];
  onSave: (header: WebhookHeaderValue) => void;
}

export function WebhookHeaderEditModal({
  header,
  existingKeys,
  onSave,
  ...props
}: WebhookHeaderEditModalProps) {
  const { close } = useModals();
  const { t } = useTranslation();
  const schema = z.object({
    key: z
      .string()
      .trim()
      .min(1, t("errors:field_required"))
      .regex(/^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/, t("core:webhook_header_invalid"))
      .refine(
        (value) =>
          !["host", "content-length", "connection", "transfer-encoding", "upgrade"].includes(
            value.toLowerCase()
          ) &&
          !value.toLowerCase().startsWith("proxy-") &&
          value.toLowerCase() !== "x-webhook-signature",
        t("core:webhook_header_forbidden")
      )
      .refine(
        (value) => !existingKeys.some((key) => key.toLowerCase() === value.toLowerCase()),
        t("core:webhook_header_duplicate")
      ),
    value: z
      .string()
      .min(1, t("errors:field_required"))
      .refine((value) => !/[\r\n]/.test(value), t("core:webhook_header_value_invalid")),
  });
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { key: header?.key ?? "", value: header?.value ?? "" },
  });

  const onSubmit = handleSubmit((data) => {
    onSave({ key: data.key.trim(), value: data.value });
    close(props.id);
  });

  return (
    <Modal
      {...props}
      title={
        header ? t("core:webhook_header_edit") : t("workflow:action_type_http_request_add_header")
      }
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Input
            {...register("key")}
            label={t("core:webhook_header_name")}
            required
            autoFocus
            error={errors.key?.message}
          />
          <Input
            {...register("value")}
            label={t("core:webhook_header_value")}
            required
            error={errors.value?.message}
          />
        </Stack>
      </form>
    </Modal>
  );
}
